import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Home,
  Bot,
  Loader2,
  CheckSquare,
  Users,
  Mail,
  Layout,
  Activity,
  Wallet,
  FolderOpen,
  HardDrive,
  BarChart3,
  ScrollText,
  ListOrdered,
  Receipt,
} from "lucide-react";

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavMain } from "@/components/navigation/nav-main";
import { NavUser } from "@/components/navigation/nav-user";
import { ThemeToggle } from "@/components/navigation/theme-toggle";
import { adminSessionUtils, signOut, type User } from "@/lib/auth-client";
import { useCanWrite } from "@/components/auth/admin-auth-guard";
import GenieLogo from "@/components/ui/icon/icons/GenieLogo";

// Admin navigation data
const data = {
  navMain: [
    {
      title: "Dashboard",
      url: "/dashboard",
      icon: Home,
      isActive: true,
    },
    {
      title: "Users",
      url: "/dashboard/users",
      icon: Users,
    },
    {
      title: "Canvas",
      url: "/dashboard/canvas",
      icon: Layout,
    },
    {
      title: "Projects",
      url: "/dashboard/projects",
      icon: FolderOpen,
    },
    {
      title: "Jobs",
      url: "/dashboard/jobs",
      icon: Activity,
    },
    {
      title: "Model Management",
      url: "/dashboard/model-management",
      icon: Bot,
    },
    {
      title: "Pricing",
      url: "/dashboard/pricing",
      icon: Receipt,
    },
    {
      title: "Finance",
      url: "/dashboard/wallets",
      icon: Wallet,
      items: [
        {
          title: "Wallets",
          url: "/dashboard/wallets",
        },
        {
          title: "Subscriptions",
          url: "/dashboard/subscriptions",
        },
        {
          title: "Earnings",
          url: "/dashboard/earnings",
        },
      ],
    },
    {
      title: "Files",
      url: "/dashboard/files",
      icon: HardDrive,
    },
    {
      title: "Waitlist",
      url: "/dashboard/waitlist",
      icon: ListOrdered,
    },
    {
      title: "Invites",
      url: "/dashboard/invites",
      icon: Mail,
    },
    {
      title: "Analytics",
      url: "/dashboard/analytics/models",
      icon: BarChart3,
      items: [
        {
          title: "Model Usage",
          url: "/dashboard/analytics/models",
        },
      ],
    },
    {
      title: "Platform Status",
      url: "/dashboard/todo",
      icon: CheckSquare,
      items: [
        {
          title: "Summary",
          url: "/dashboard/todo",
        },
        {
          title: "Categories",
          url: "/dashboard/todo/categories",
        },
      ],
    },
    {
      title: "Audit Logs",
      url: "/dashboard/audit-logs",
      icon: ScrollText,
    },
  ],
};

function SidebarLogoHeader() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";

  return (
    <SidebarHeader>
      <div className={`flex items-center justify-between transition-all duration-200 ${isCollapsed ? "px-2 py-2" : "px-4 py-3"}`}>
        <GenieLogo className={`text-primary transition-all duration-200 ${isCollapsed ? "h-5 w-5" : "h-8 w-8"}`} />
        {!isCollapsed && <ThemeToggle />}
      </div>
    </SidebarHeader>
  );
}

export function DashboardSidebar() {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const canWrite = useCanWrite();

  useEffect(() => {
    loadUser();
  }, []);

  async function loadUser() {
    try {
      const currentUser = await adminSessionUtils.getCurrentAdmin();
      setUser(currentUser);
    } catch (error) {
      console.error('Failed to load user:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleLogout() {
    try {
      await signOut();
      navigate({ to: '/login' });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  }

  if (isLoading) {
    return (
      <Sidebar collapsible="icon">
        <SidebarLogoHeader />
        <SidebarContent>
          <div className="flex items-center justify-center p-4">
            {isCollapsed ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading...</span>
              </div>
            )}
          </div>
        </SidebarContent>
        <SidebarFooter />
        <SidebarRail />
      </Sidebar>
    );
  }

  return (
    <Sidebar collapsible="icon">
      <SidebarLogoHeader />
      <SidebarContent>
        {!canWrite && !isCollapsed && (
          <div className="mx-3 mt-1 rounded-md bg-muted px-3 py-1.5 text-center text-xs font-medium text-muted-foreground">
            Read-only
          </div>
        )}
        <NavMain items={data.navMain} />
      </SidebarContent>
      <SidebarFooter>
        {user && (
          <NavUser
            user={{
              name: user.name || user.email?.split('@')[0] || 'Admin',
              email: user.email || '',
              avatar: user.image || undefined
            }}
            onLogout={handleLogout}
          />
        )}
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
